"use client";

import React, { useState } from "react";
import * as XLSX from "xlsx";
import { buildCompareTableData } from "@/utils/comparePageFunctions";
import { CompareApiResponse, PortfolioApiItem } from "@/type/comparePageTypes";
import type { jsPDF } from "jspdf";
import Image from "next/image";
import { useLoginModal } from "@/utils/useLoginModal";
import { ROOT_URL } from "@/utils/gloableVariables";

type Props = {
  compareFundData: CompareApiResponse;
  portfolioDataArray: PortfolioApiItem[];
};

type PdfOptions = {
  margin: number;
  filename: string;
  image: {
    type: "jpeg" | "png" | "webp";
    quality: number;
  };
  html2canvas: {
    scale: number;
    useCORS: boolean;
  };
  jsPDF: {
    unit: "mm";
    format: "a4";
    orientation: "landscape" | "portrait";
  };
  pagebreak?: {
    mode: Array<"avoid-all" | "css" | "legacy">;
  };
};

export default function DownloadCompareReport({ compareFundData, portfolioDataArray }: Props) { 
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [isGeneratingXLS, setIsGeneratingXLS] = useState<boolean>(false);
  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const buildPdfTableHtml = () => {
    const tableAllData = buildCompareTableData(compareFundData, portfolioDataArray, true);

    const { columns, sections } = tableAllData;

    const totalCols = columns.length + 1;
    const colWidth = 100 / totalCols;

    let html = `<style>
                    table {
                    border-collapse: collapse;
                    width: 100%;
                    table-layout: fixed;
                    font-size: 11px;
                    }

                    th, td {
                    border: 1px solid #CBD5E1;
                    padding: 6px;
                    word-wrap: break-word;
                    }

                    th {
                    background: #F1F5F9;
                    font-weight: 700;
                    text-align: center;
                    }

                    td.metric {
                    font-weight: 600;
                    background: #FAFBFE;
                    }

                    tr {
                    break-inside: avoid;
                    page-break-inside: avoid;
                    }

                    .compare-table-section {
                    background: #E2E8F0;
                    font-weight: 700;
                    text-transform: uppercase;
                    }

                    .wrapper {
                    width: 100%;
                    padding: 18px;
                    font-family: Intel, sans-serif;
                    }

                    col {
                    width: ${colWidth}%;
                    }
                </style>

                    <div class="wrapper"> 
                      <div class="w-full mb-3.5 text-center text-[#0e6799] text-2.5xl">Compare Funds Table Report</div>
                        <table>
                          <colgroup>
                `;

    for (let i = 0; i < totalCols; i++) {
      html += `<col />`;
    }

    html += ` </colgroup>
                <thead>
                  <tr>
                    <th>Metric</th>
              `;

    columns.forEach((col) => {
      html += `<th>${col.name}</th>`;
    });

    html += `     </tr>
                </thead>
              <tbody>
            `;

    sections.forEach((section) => {
      html += ` <tr class="compare-table-section">
                     <td colspan="${totalCols}">
                     ${section.title}
                    </td>
                </tr>
                    `;

      section.rows.forEach((row) => {
        html += `<tr>`;

        html += `<td class="metric">${row.label}</td>`;

        row.values.forEach((val) => {
          html += `<td style="text-align:center">${val}</td>`;
        });

        html += `</tr>`;
      });
    });

    html += `     </tbody>
                    </table>
                </div>
                `;

    return html;
  };

  const handlePDF = async () => {
    if (!isLoggedIn) {
      open();
      setModalKey("compare-pdf-download");
      return;
    }

    const html = buildPdfTableHtml();
    setIsGeneratingPDF(true);

    const container = document.createElement("div");
    container.innerHTML = html;

    const options: PdfOptions = {
      margin: 10,
      filename: "compare-fund-report.pdf",
      pagebreak: {
        mode: ["avoid-all", "css"],
      },
      image: {
        type: "jpeg",
        quality: 0.98,
      },
      html2canvas: {
        scale: 2,
        useCORS: true,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "landscape",
      },
    };

    const html2pdf = (await import("html2pdf.js")).default;

    await html2pdf()
      .set(options)
      .from(container)
      .toPdf()
      .get("pdf")
      .then((pdf: jsPDF) => {
        const pageCount = pdf.getNumberOfPages();

        const viewportWidth = pdf.internal.pageSize.getWidth();
        const viewportHeight = pdf.internal.pageSize.getHeight();

        pdf.setPage(1);

        pdf.setFontSize(14);
        pdf.setTextColor(32, 32, 32); // MySIPonline color

        const headingText = "MYSIPonline";

        const headingWidth = pdf.getTextDimensions(headingText).w;

        const headingX = (viewportWidth - headingWidth) / 2;
        const headingY = 12;

        pdf.text(headingText, headingX, headingY);

        pdf.setPage(pageCount);

        pdf.setFontSize(16);

        const label = "Report by";
        const brand = " MySIPonline";

        const labelWidth = pdf.getTextDimensions(label).w;
        const brandWidth = pdf.getTextDimensions(brand).w;

        const totalWidth = labelWidth + brandWidth;

        const paddingX = 20;
        const paddingY = 18;

        const x = viewportWidth - totalWidth - paddingX;
        const y = viewportHeight - paddingY;

        pdf.setTextColor(32, 32, 32);
        pdf.text(label, x, y);

        pdf.setTextColor(10, 103, 155);
        pdf.textWithLink(brand, x + labelWidth, y, {
          url: "https://www.mysiponline.com",
        });

        pdf.save("mysiponline-compare-fund-report.pdf");
      })
      .finally(() => {
        setIsGeneratingPDF(false);
      });
  };

  const handleExcel = (): void => {
    if (!isLoggedIn) {
      open();
      setModalKey("compare-page-excel-download");
      return;
    }

    setIsGeneratingXLS(true);

    try {
      const tableAllData = buildCompareTableData(compareFundData, portfolioDataArray, true);

      const { columns, sections } = tableAllData;

      type ExcelRow = Record<string, string>;

      const rows: ExcelRow[] = [];

      sections.forEach((section) => {
        // Section heading row
        rows.push({ METRIC: section.title });

        section.rows.forEach((row) => {
          const r: ExcelRow = { METRIC: row.label };

          columns.forEach((col, idx) => {
            r[col.name] = row.values[idx] ?? "";
          });

          rows.push(r);
        });

        // Spacer row
        rows.push({});
      });

      const sheet = XLSX.utils.json_to_sheet(rows);
      const book = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(book, sheet, "Compare");

      XLSX.writeFile(book, "compare-report.xlsx");
    } finally {
      setIsGeneratingXLS(false);
    }
  };

  return (
    <div className="flex w-fit mx-auto items-center gap-3 mb-6">
      <span className="font-medium text-[#004B6B]">Download in</span>
      <button onClick={handlePDF} disabled={isGeneratingPDF} aria-label="Download PDF Report File" className="w-11 h-9 flex items-center justify-center rounded-lg border border-[#56565640] relative">
        {isGeneratingPDF ? <div className="transform w-5 h-5 border-[3px] border-solid border-transparent border-t-[#0c3e9f] rounded-full animate-spin" /> : <Image src={`${ROOT_URL}images/swp-calculator/pdf.svg`} alt="PDF Icon" width={20} height={24} className="w-5" />}
      </button>

      <button onClick={handleExcel} disabled={isGeneratingXLS} aria-label="Download XLSX Report File" className="w-11 h-9 flex items-center justify-center rounded-lg border border-[#56565640] relative">
        {isGeneratingXLS ? <div className="transform w-5 h-5 border-[3px] border-solid border-transparent border-t-[#0c3e9f] rounded-full animate-spin" /> : <Image src={`${ROOT_URL}images/swp-calculator/excel.svg`} alt="Excel Icon" width={20} height={24} className="w-5" />}
      </button>
    </div>
  );
}
